/**
 * The panel's shell: what one navigates through and what state the account is in.
 *
 * It is what wraps the content and does not depend on it — the sidebar, the Meta setup and
 * the connection label paint the same with zero rules or with fifty.
 */

import type { ConfigurationStatus, InstagramProfile, OnboardingStatus } from "../api/types";

/**
 * The panel's sections, each under its own address.
 *
 * `shortLabel` is what shows in the mobile bottom bar, where the long names do not fit.
 */
export const NAV_ITEMS = [
  { label: "Inicio", shortLabel: "Inicio", icon: "layout-panel-left", path: "/" },
  { label: "Automatizaciones", shortLabel: "Reglas", icon: "zap", path: "/automatizaciones" },
  { label: "Publicaciones", shortLabel: "Posts", icon: "grid-2x2", path: "/publicaciones" },
  { label: "Conversaciones", shortLabel: "Chats", icon: "messages-square", path: "/conversaciones" },
  { label: "Personas", shortLabel: "Personas", icon: "users", path: "/personas" },
  { label: "Actividad", shortLabel: "Registro", icon: "activity", path: "/actividad" },
  { label: "Ajustes", shortLabel: "Ajustes", icon: "settings", path: "/ajustes" },
] as const;

/**
 * The phone's bottom bar holds four sections around the "+" — the ones visited every day —
 * and the rest open from "Más".
 */
export const MOBILE_PRIMARY = ["/", "/automatizaciones", "/publicaciones", "/conversaciones"] as const;
export const MOBILE_SECONDARY = ["/personas", "/actividad", "/ajustes"] as const;

/** The section an address belongs to: `/automatizaciones/12` is still Automatizaciones. */
export function sectionOf(pathname: string): string {
  return pathname.split("/")[1] ?? "";
}

/**
 * The script for setting Meta up.
 *
 * It is the only screen somebody who has never touched the developer console will see, so
 * every step says exactly where each value goes, and the panel shows the ones it knows
 * itself — return URL, webhook and token — instead of asking anybody to invent them.
 */
export function toSetupView(
  config: Partial<Pick<ConfigurationStatus, "appId" | "appSecret">> = {},
  onboarding: Partial<Pick<OnboardingStatus, "redirectUri" | "webhookUrl" | "verifyToken">> | null = null,
  { editKeys = false }: { editKeys?: boolean } = {},
) {
  const hasKeys = Boolean(config.appId && config.appSecret);
  const stepKeys = editKeys || !hasKeys;
  const redirectUri = onboarding?.redirectUri ?? "";
  const webhookUrl = onboarding?.webhookUrl ?? "";
  const verifyToken = onboarding?.verifyToken ?? "";

  const row = (label: string, value: string) => (value ? [{ label, value }] : []);
  const steps = (texts: string[]) => texts.map((text, index) => ({ index: String(index + 1).padStart(2, "0"), text }));

  if (stepKeys) {
    return {
      stepKeys: true,
      stepConnect: false,
      eyebrow: "CONFIGURACIÓN · PASO 1 DE 2",
      title: "Conecta tu cuenta de Instagram",
      subtitle:
        "Hace falta una app de Meta con el producto «Instagram». Pega aquí sus dos claves y el panel se encarga del resto.",
      steps: steps([
        "Crea una app en developers.facebook.com/apps y añádele el producto «Instagram».",
        "Abre «Configuración de la API con inicio de sesión de Instagram».",
        "Pega esta URL en «URL de redireccionamiento de OAuth» y copia de ahí el App ID y el App Secret.",
      ]),
      rows: row("URL DE REDIRECCIONAMIENTO DE OAUTH", redirectUri),
    };
  }

  return {
    stepKeys: false,
    stepConnect: true,
    eyebrow: "CONFIGURACIÓN · PASO 2 DE 2",
    title: "Autoriza tu cuenta",
    subtitle: "Registra el webhook con estos valores en la consola de Meta y después autoriza la cuenta profesional.",
    steps: steps([
      "En «Webhooks» de Instagram pega la URL de devolución de llamada y el token de verificación.",
      "Suscríbete a «comments», «messages» y «messaging_postbacks» (el último trae los botones pulsados).",
      "Vuelve aquí y pulsa «Conectar con Instagram» para autorizar la cuenta.",
    ]),
    rows: [
      ...row("URL DE DEVOLUCIÓN DE LLAMADA", webhookUrl),
      ...row("TOKEN DE VERIFICACIÓN", verifyToken),
      ...row("URL DE REDIRECCIONAMIENTO DE OAUTH", redirectUri),
    ],
  };
}

export function toConnectionView(
  config: Partial<Pick<ConfigurationStatus, "accessToken" | "instagramAccountId" | "dryRun">> | null | undefined,
  profile: Partial<Pick<InstagramProfile, "username" | "profilePictureUrl">> | null | undefined,
) {
  const connected = Boolean(config?.accessToken && config?.instagramAccountId && !config?.dryRun);
  const username = String(profile?.username ?? "")
    .trim()
    .replace(/^@+/, "");
  const profileHandle = username ? `@${username}` : connected ? "Cuenta de Instagram" : "Modo simulación";
  return {
    connected,
    username,
    profileHandle,
    profileImage: profile?.profilePictureUrl || "/InstaGain.png",
    profileAlt: username ? `Foto de perfil de @${username}` : "Cuenta de Instagram",
    profileLabel: connected ? "Cuenta conectada" : "Modo local / Simulación",
    connectionStatus: connected ? "Meta está conectado" : "Modo seguro: simulación",
    connectionBadgeLabel: connected ? "CONECTADO" : "SIMULACIÓN",
    connectionSub: connected
      ? username
        ? `@${username} · sistema activo y escuchando`
        : "Cuenta activa y escuchando comentarios"
      : "Conecta el token para activar envíos reales",
  };
}
